import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const CASES = [
  { tag: "BUILDER FLOORS · SOUTH DELHI", title: "Sold-Out in 90 Days", big: "All Units Booked", sub: "312 qualified leads · ₹427 CPL", img: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=900&q=70&auto=format&fit=crop" },
  { tag: "HIGH-RISE TOWNSHIP · GURUGRAM", title: "Tower Launch to Lakh+", big: "1.04L leads", sub: "4.6x ROAS · 47 site visits/week", img: "https://images.unsplash.com/photo-1486325212027-8081e485255e?w=900&q=70&auto=format&fit=crop" },
  { tag: "LUXURY APARTMENTS · NOIDA + DUBAI", title: "NRI Pipeline Builder", big: "Multi-City Pipeline", sub: "Dubai + Singapore funnels", img: "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=900&q=70&auto=format&fit=crop" },
  { tag: "MANAGED FARMS · SHUKRATAL", title: "Farmland Flip", big: "118 acres sold", sub: "HNI investor narrative · 3.2x ROAS", img: "https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=900&q=70&auto=format&fit=crop" },
  { tag: "LOGISTICS · SONIPAT NH-44", title: "Industrial Land Sell-Out", big: "Fully Sold Out", sub: "LinkedIn ABM + family-office outreach", img: "https://images.unsplash.com/photo-1553413077-190dd305871c?w=900&q=70&auto=format&fit=crop" },
  { tag: "SECOND HOMES · UTTARAKHAND", title: "Hill Villas Sold Out", big: "All Villas Booked", sub: "Lifestyle reels · Delhi/Mumbai HNI", img: "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=900&q=70&auto=format&fit=crop" },
  { tag: "FLOORS · DELHI · GGN · NOIDA", title: "Builder Floors Bookings", big: "208 bookings", sub: "Hyperlocal Meta + Google · ₹389 CPL", img: "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=900&q=70&auto=format&fit=crop" },
  { tag: "HIGH-RISE · GREATER FARIDABAD", title: "Faridabad Tower Launch", big: "612 site visits", sub: "60-day pre-launch · ₹186 CPL", img: "https://images.unsplash.com/photo-1551361415-69c87624334f?w=900&q=70&auto=format&fit=crop" },
  { tag: "PARCELS · NAUGAON + GGN FRINGE", title: "Freehold Land Closures", big: "9 Family Offices Closed", sub: "Discreet HNI outreach", img: "https://images.unsplash.com/photo-1572120360610-d971b9d7767c?w=900&q=70&auto=format&fit=crop" },
];

export default function CaseStudies() {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start", slidesToScroll: 1 });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [snapCount, setSnapCount] = useState(0);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    onSelect();
    setSnapCount(emblaApi.scrollSnapList().length);
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);

    // autoplay
    const t = setInterval(() => emblaApi.scrollNext(), 5500);
    return () => {
      clearInterval(t);
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi]);

  return (
    <section id="work" data-testid="case-studies-section" className="relative py-24 sm:py-32 border-t border-white/5 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-8 mb-14">
          <div>
            <div className="font-mono text-xs text-[#d4ff4f] mb-3 tracking-[0.2em]">#असली_कहानियाँ</div>
            <h2 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.95]">
              Case studies.<br />
              <span className="italic text-[#d4ff4f]">Real numbers.</span>
            </h2>
          </div>
          <p className="max-w-md text-white/60 self-end leading-relaxed">
            We don't share screenshots of "engagement". We share <span className="text-white">booked inventory</span>, weekly site-visit counts and revenue per rupee spent.
          </p>
        </div>

        {/* Slider */}
        <div className="relative">
          <div className="overflow-hidden" ref={emblaRef} data-testid="case-slider">
            <div className="flex gap-5">
              {CASES.map((c, i) => (
                <article
                  key={i}
                  data-testid={`case-${i}`}
                  className="card-hover relative overflow-hidden rounded-2xl border border-white/10 group shrink-0 grow-0 basis-[85%] sm:basis-[48%] lg:basis-[32%] aspect-[4/5]"
                >
                  <img
                    src={c.img}
                    alt={c.title}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:scale-105 group-hover:opacity-80 transition-all duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a0b08] via-[#0a0b08]/60 to-transparent" />
                  <div className="absolute top-4 left-4 right-4 flex items-start justify-between gap-2">
                    <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/70">{c.tag}</div>
                    <span className="font-mono text-[10px] text-[#d4ff4f]/80">{String(i + 1).padStart(2, "0")}/{CASES.length}</span>
                  </div>
                  <div className="absolute bottom-0 inset-x-0 p-5">
                    <h3 className="font-display text-2xl mb-4 tracking-tight">{c.title}</h3>
                    <div className="font-display text-3xl text-[#d4ff4f] tracking-tight">{c.big}</div>
                    <div className="text-xs text-white/60 mt-1">{c.sub}</div>
                  </div>
                </article>
              ))}
            </div>
          </div>

          {/* Controls */}
          <button
            data-testid="case-prev"
            onClick={scrollPrev}
            aria-label="Previous case study"
            className="absolute -left-4 sm:-left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full border border-white/15 bg-[#0a0b08]/80 hover:bg-[#d4ff4f] hover:text-black flex items-center justify-center transition z-10"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            data-testid="case-next"
            onClick={scrollNext}
            aria-label="Next case study"
            className="absolute -right-4 sm:-right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full border border-white/15 bg-[#0a0b08]/80 hover:bg-[#d4ff4f] hover:text-black flex items-center justify-center transition z-10"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Dots */}
        <div className="mt-8 flex items-center justify-center gap-2" data-testid="case-dots">
          {Array.from({ length: snapCount }).map((_, idx) => (
            <button
              key={idx}
              aria-label={`Go to case ${idx + 1}`}
              onClick={() => emblaApi && emblaApi.scrollTo(idx)}
              className={`h-1.5 rounded-full transition-all ${
                idx === selectedIndex ? "w-10 bg-[#d4ff4f]" : "w-2 bg-white/20 hover:bg-white/40"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
